import type { User } from "@/types";
import type { AddonPermission } from "@/stores/permissions";
import type { SubscriptionPermission } from "@/features/settings/api/subscriptionService";

function hasRole(user: User | null, roles: number[]): boolean {
  return user != null && roles.includes(user.role);
}

/** These roles use the inviting company's access, rather than their own plan. */
export function isSponsoredRole(role: number | undefined): boolean {
  return role != null && [5, 6, 7].includes(role);
}

/** Roles 1-2 = Administrator or Manager. */
export function isAdminOrManager(user: User | null): boolean {
  return hasRole(user, [1, 2]);
}

/**
 * True when every required permission code is present in the granted set.
 * Mirrors the legacy `checkPermissions` helper.
 */
export function checkPermissions(
  required: string[],
  granted: string[],
): boolean {
  return required.every((permission) => granted.includes(permission));
}

/**
 * True when the plan feature (e.g. "EMISSIONS", "SRM") is included in the
 * active subscription. A missing `required` feature means no gate.
 *
 * When `count` and `addonPermissions` are provided the function also sums
 * plan + addon usage limits and denies when `count >= usageLimit`.
 * Pass `count = -1` to skip the usage-limit check (existence-only mode).
 */
export function checkSubscriptionPermission(
  required: string | undefined,
  planPermissions: string[],
  count?: number,
  addonPermissions?: AddonPermission[],
  planGrants?: SubscriptionPermission[],
): boolean {
  if (!required) return true;

  if (planGrants) {
    const base = planGrants.filter((grant) => grant.name === required);
    const addons = (addonPermissions ?? []).filter(
      (grant) => grant.name === required && Number(grant.quantity) > 0,
    );
    if (!base.length && !addons.length) return false;
    if (
      count == null ||
      count === -1 ||
      base.some((grant) => grant.usageLimit == null) ||
      addons.some((grant) => grant.usage_limit == null)
    )
      return true;
    const limit =
      base.reduce((sum, grant) => sum + Math.max(0, grant.usageLimit ?? 0), 0) +
      addons.reduce(
        (sum, grant) =>
          sum + Math.max(0, grant.usage_limit ?? 0) * Number(grant.quantity),
        0,
      );
    return count < limit;
  }

  let hasPermission = planPermissions.includes(required);
  let usageLimit = 0;

  if (addonPermissions?.length) {
    for (const addon of addonPermissions) {
      if (addon.name === required && Number(addon.quantity) > 0) {
        hasPermission = true;
        if (addon.usage_limit != null)
          usageLimit += addon.usage_limit * Number(addon.quantity);
      }
    }
  }

  if (count != null && count !== -1 && usageLimit > 0 && usageLimit <= count) {
    hasPermission = false;
  }

  return hasPermission;
}

/**
 * Sum the total usage limit for a feature across plan permissions and
 * addon permissions. Returns 0 when no limit is defined.
 *
 * Mirrors the legacy `checkSubscriptionLimit` helper.
 */
export function checkSubscriptionLimit(
  feature: string,
  addonPermissions: AddonPermission[],
  planGrants?: SubscriptionPermission[],
): number {
  if (
    planGrants?.some(
      (grant) => grant.name === feature && grant.usageLimit == null,
    ) ||
    addonPermissions?.some(
      (grant) =>
        grant.name === feature &&
        Number(grant.quantity) > 0 &&
        grant.usage_limit == null,
    )
  )
    return 0;
  return (addonPermissions ?? []).reduce(
    (sum, addon) => {
      if (addon.name === feature && addon.usage_limit) {
        return sum + addon.usage_limit * (addon.quantity ?? 1);
      }
      return sum;
    },
    (planGrants ?? []).reduce(
      (sum, grant) =>
        grant.name === feature ? sum + Math.max(0, grant.usageLimit ?? 0) : sum,
      0,
    ),
  );
}
